import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { nav, brand } from "@/config/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      // Hide-on-scroll-down / reappear-on-scroll-up (mobile behavior).
      if (y > lastY + 6 && y > 140) setHidden(true);
      else if (y < lastY - 6) setHidden(false);
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding,transform] duration-500 md:!translate-y-0 ${
        hidden && !open ? "-translate-y-full" : "translate-y-0"
      } ${
        scrolled || open
          ? "bg-dh-bg/85 backdrop-blur-xl border-b border-dh-cream/10 py-3"
          : "bg-transparent border-b border-transparent py-5"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="group flex items-center gap-3.5"
          aria-label="Dhamma Publishing House — home"
        >
          <img
            src={brand.crest}
            alt="Dhamma Publishing House crest"
            className="h-10 w-10 shrink-0 object-contain transition-transform duration-700 group-hover:scale-105 sm:h-11 sm:w-11"
          />
          <span className="flex flex-col leading-none">
            <span className="font-display text-xl tracking-[0.02em] text-dh-cream transition-colors duration-500 group-hover:text-dh-gold sm:text-2xl">
              Dhamma
            </span>
            <span className="mt-1 font-body text-[9px] uppercase tracking-ultra text-dh-gold/80">
              Publishing House
            </span>
          </span>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden md:flex items-center gap-8 lg:gap-10">
          {nav.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative font-body text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 hover:text-dh-cream ${
                    isActive ? "text-dh-cream" : "text-dh-muted"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative inline-block pb-1">
                    {item.label}
                    <span
                      className={`absolute -bottom-0.5 left-0 h-px bg-dh-gold transition-all duration-500 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Mobile toggle */}
        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center text-dh-cream"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-panel"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-dh-bg/95 backdrop-blur-xl border-t border-dh-cream/10"
          >
            <ul className="flex flex-col px-6 py-6">
              {nav.map((item, i) => (
                <motion.li
                  key={item.to}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                  className="border-b border-dh-cream/5 last:border-0"
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block py-4 font-display text-2xl ${
                        isActive ? "text-dh-gold" : "text-dh-cream"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
